import type { Row } from "@rocicorp/zero";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import type { CrossResourceAvailabilityRuleId } from "../../shared/rbac/crossResourceAvailability";
import { isSupplierAvailableFromGrants } from "../../shared/rbac/crossResourceAvailability";
import type { GrantRow } from "../../shared/rbac/grantQueries";
import type { ShareableScopeKind } from "../../shared/rbac/permissions";
import type { GrantEntry, RoleOption } from "../../shared/rbac/roles";
import {
  useAffectedChatCount,
  useConnectorDisconnectImpact,
} from "../hooks/useConnectorDisconnectImpact";
import { useGroupMemberships } from "../hooks/useGroupMemberships";
import { usePrincipalModals } from "../hooks/usePrincipalModals";
import { useScopeGrants } from "../hooks/useScopeGrants";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { collectPrincipalGrants } from "../lib/connector";
import { countSpaceMembers } from "../lib/countSpaceMembers";
import { deriveScopeGrantDelta } from "../lib/spaceGrantDelta";
import { defaultRoleForScope } from "../lib/defaultRoleForScope";
import { queries } from "../../shared/queries";
import type {
  SpaceGrant,
  SpacePrincipalRoles,
} from "../lib/spaceSupplierGrants";
import {
  grantsWithoutSpacePrincipals,
  removedPrincipalPreviews,
  spacesGainingSupplierAccess,
} from "../lib/spaceSupplierAccess";
import { ConfirmModal } from "./ConfirmModal";
import { DisconnectWarning, RemovalDescription } from "./DisconnectWarning";
import type { PrincipalPreview } from "./PrincipalPreviewList";
import {
  type ScopeAccessPendingHandle,
  ScopeAccessTable,
} from "./ScopeAccessTable";
import { countHiddenScopesOfKind } from "./Settings/lib/countHiddenScopes";
import { SettingsCard as Card, SettingsHeading } from "./SettingsCard";
import { Text } from "./Text";
import { UsableSpacesList } from "./UsableSpacesList";

const TabRoot = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// The resource's own scope grants, broad enough for both the cross-resource
// availability check (GrantRow) and useScopeGrants (which also reads
// customRoleId/serviceAccountId).
type ScopeGrant = GrantRow & {
  serviceAccountId?: string | null;
  customRoleId?: string | null;
};

type Props = {
  scopeId: string;
  scopeKind: ShareableScopeKind;
  scopeGrants: readonly ScopeGrant[];
  supplierRelation: CrossResourceAvailabilityRuleId;
  roleDefaultsForSpace: (
    spaceGrants: readonly SpaceGrant[],
    roleOptions: readonly RoleOption[]
  ) => SpacePrincipalRoles;
  resourceLabel: string;
  accessDescription: string;
  groupAccess: Parameters<typeof collectPrincipalGrants>[0];
  canManage: boolean;
  onEdit?: () => void;
  stagedGrantsRef?: { current: ScopeAccessPendingHandle | null };
  // When set, edits stage into this local grant set (for composing a share
  // request) instead of committing to the scope. `scopeGrants` still supplies
  // the "spaces gaining access" preview against the connector's real grants.
  localGrants?: GrantEntry[];
  onChangeLocalGrants?: (grants: GrantEntry[]) => void;
  newPrincipalIds?: ReadonlySet<string>;
  guardCommit?: (
    grants: GrantEntry[],
    proceed: (grants: GrantEntry[]) => void
  ) => void;
  guardModal?: ReactNode;
  accessFooter?: ReactNode;
  children?: ReactNode;
};

export function SupplierAccessTab({
  scopeId,
  scopeKind,
  scopeGrants,
  supplierRelation,
  roleDefaultsForSpace,
  resourceLabel,
  accessDescription,
  groupAccess,
  canManage,
  onEdit,
  stagedGrantsRef,
  localGrants,
  onChangeLocalGrants,
  newPrincipalIds,
  guardCommit,
  guardModal,
  accessFooter,
  children,
}: Props) {
  const z = useZero();
  const { onOpenUser, onOpenGroup, onOpenServiceAccount } =
    usePrincipalModals();
  const [currentUser] = useQuery(queries.currentUser());
  const [spaces] = useQuery(queries.spaces({ settingsView: true }));
  const [allGroups] = useQuery(queries.selectableUserGroups());
  const [allUsers] = useQuery(queries.allUsers());
  const [serviceAccounts] = useQuery(queries.serviceAccounts());
  const [customRoles] = useQuery(queries.allRolesLite());
  const { userGroupIds: userGroupMemberships, memberUserIds } =
    useGroupMemberships();
  const displaySpaceName = useSpaceDisplayName();

  const local = localGrants != null && onChangeLocalGrants != null;
  const {
    allGrants,
    assignedGroups,
    assignedUsers,
    roleOptions,
    commitGrants,
    removeGroup,
    removeUser,
    managerConfirmModal,
  } = useScopeGrants(
    local
      ? {
          scopeKind,
          allGroups,
          allUsers,
          customRoles,
          grants: localGrants,
          onCommit: onChangeLocalGrants,
        }
      : {
          scopeId,
          scopeKind,
          rawGrants: scopeGrants,
          allGroups,
          allUsers,
          customRoles,
          viewerId: currentUser?.id,
          resourceLabel,
          guardCommit,
        }
  );

  const groupById = useMemo(
    () => new Map(allGroups.map((g) => [g.id, g] as const)),
    [allGroups]
  );
  const userById = useMemo(
    () => new Map(allUsers.flatMap((u) => (u ? [[u.id, u] as const] : []))),
    [allUsers]
  );
  const serviceAccountById = useMemo(
    () => new Map(serviceAccounts.map((sa) => [sa.id, sa] as const)),
    [serviceAccounts]
  );
  const customRoleById = useMemo(
    () => new Map(customRoles.map((r) => [r.id, r])),
    [customRoles]
  );

  const sharedSpaces = useMemo(
    () => spaces.filter((s) => s.mode !== "personal"),
    [spaces]
  );

  const usableSpaces = useMemo(
    () =>
      sharedSpaces.flatMap((space) => {
        if (
          space.roleGrantsAsScope.length === 0 ||
          !isSupplierAvailableFromGrants(
            supplierRelation,
            space.roleGrantsAsScope,
            scopeGrants,
            userGroupMemberships
          )
        ) {
          return [];
        }
        return [
          {
            ...space,
            usableMemberCount: countSpaceMembers(
              space.roleGrantsAsScope,
              memberUserIds
            ),
          },
        ];
      }),
    [
      scopeGrants,
      memberUserIds,
      sharedSpaces,
      supplierRelation,
      userGroupMemberships,
    ]
  );

  const usableSpaceIds = useMemo(
    () => new Set(usableSpaces.map((space) => space.id)),
    [usableSpaces]
  );

  const scopeGrantEntries = useMemo(
    () =>
      scopeGrants
        .filter((g) => g.userGroupId || g.userId || g.serviceAccountId)
        .map((g) => ({
          userGroupId: g.userGroupId ?? undefined,
          userId: g.userId ?? undefined,
          serviceAccountId: g.serviceAccountId ?? undefined,
          builtinRoleId: g.builtinRoleId ?? undefined,
          customRoleId: g.customRoleId ?? undefined,
        })),
    [scopeGrants]
  );

  const removableSpaceIds = useMemo(() => {
    if (!canManage) return new Set<string>();
    const ids = new Set<string>();
    for (const space of usableSpaces) {
      if (
        grantsWithoutSpacePrincipals(scopeGrantEntries, space.roleGrantsAsScope)
          .removed.length > 0
      )
        ids.add(space.id);
    }
    return ids;
  }, [canManage, usableSpaces, scopeGrantEntries]);

  const [removingSpaceId, setRemovingSpaceId] = useState<string | null>(null);
  const removingSpace = removingSpaceId
    ? (usableSpaces.find((s) => s.id === removingSpaceId) ?? null)
    : null;
  const spaceRemoval = useMemo(
    () =>
      removingSpace
        ? grantsWithoutSpacePrincipals(
            scopeGrantEntries,
            removingSpace.roleGrantsAsScope
          )
        : null,
    [removingSpace, scopeGrantEntries]
  );
  const removedPrincipals: PrincipalPreview[] = useMemo(
    () =>
      spaceRemoval
        ? removedPrincipalPreviews(spaceRemoval.removed, {
            groupById,
            userById,
            serviceAccountById,
          })
        : [],
    [spaceRemoval, groupById, userById, serviceAccountById]
  );

  const computeRemovalImpact = useConnectorDisconnectImpact(
    scopeKind === "connected_app" && removingSpace ? scopeId : null
  );
  const removalImpact =
    removingSpace && spaceRemoval && scopeKind === "connected_app"
      ? computeRemovalImpact(scopeGrants, spaceRemoval.next)
      : null;
  const chatCount = useAffectedChatCount(
    scopeKind === "connected_app" ? scopeId : null,
    removalImpact?.affectedUserIds ?? []
  );

  const confirmRemoveSpace = useCallback(() => {
    if (!spaceRemoval) return;
    onEdit?.();
    const { add, remove } = deriveScopeGrantDelta(
      scopeGrantEntries,
      spaceRemoval.next
    );
    z.mutate(
      sharedMutators.patchScopeGrants({ scopeId, scopeKind, add, remove })
    );
    setRemovingSpaceId(null);
  }, [z, scopeId, scopeKind, scopeGrantEntries, spaceRemoval, onEdit]);

  const hiddenSpaceCount = useMemo(() => {
    const visibleSpaceIds = new Set(spaces.map((s) => s.id));
    return countHiddenScopesOfKind(
      collectPrincipalGrants(groupAccess),
      "space",
      visibleSpaceIds,
      customRoleById
    );
  }, [groupAccess, spaces, customRoleById]);

  // Spaces addable in the access picker; choosing one grants its members and
  // groups access at the role selected in the picker.
  const addableSpaces = useMemo(
    () =>
      sharedSpaces.flatMap((space) => {
        if (
          usableSpaceIds.has(space.id) ||
          space.roleGrantsAsScope.length === 0
        )
          return [];
        const seenGroup = new Set<string>();
        const seenUser = new Set<string>();
        const seenServiceAccount = new Set<string>();
        const groups: { id: string; name: string }[] = [];
        const userIds: string[] = [];
        const serviceAccountIds: string[] = [];
        const members: { user?: Row["user"] | null }[] = [];
        for (const g of space.roleGrantsAsScope) {
          if (g.userGroupId) {
            if (seenGroup.has(g.userGroupId)) continue;
            seenGroup.add(g.userGroupId);
            const grp = groupById.get(g.userGroupId);
            if (!grp) continue;
            groups.push({ id: grp.id, name: grp.name });
            for (const r of grp.roleGrantsAsScope)
              members.push({ user: r.user });
          } else if (g.userId) {
            const u = userById.get(g.userId);
            if (u) members.push({ user: u });
            if (!seenUser.has(g.userId)) {
              seenUser.add(g.userId);
              userIds.push(g.userId);
            }
          } else if (g.serviceAccountId) {
            if (!seenServiceAccount.has(g.serviceAccountId)) {
              seenServiceAccount.add(g.serviceAccountId);
              serviceAccountIds.push(g.serviceAccountId);
            }
          }
        }
        const { rolesByGroupId, rolesByUserId, rolesByServiceAccountId } =
          roleDefaultsForSpace(space.roleGrantsAsScope, roleOptions);
        return [
          {
            ...space,
            name: displaySpaceName(space),
            memberCount: countSpaceMembers(
              space.roleGrantsAsScope,
              memberUserIds
            ),
            groupCount: groups.length,
            groups,
            userIds,
            serviceAccountIds,
            members,
            rolesByGroupId,
            rolesByUserId,
            rolesByServiceAccountId,
          },
        ];
      }),
    [
      sharedSpaces,
      usableSpaceIds,
      displaySpaceName,
      memberUserIds,
      groupById,
      userById,
      roleOptions,
      roleDefaultsForSpace,
    ]
  );

  const isOnlyCurrentUser =
    assignedGroups.length === 0 &&
    assignedUsers.length === 1 &&
    assignedUsers[0]?.id === currentUser?.id;

  return (
    <TabRoot>
      {managerConfirmModal}
      {guardModal}
      <Card>
        <SettingsHeading>
          <Text variant="text-regular-md">Access</Text>
          <Text variant="text-small" color="var(--text-secondary)">
            {accessDescription}
          </Text>
        </SettingsHeading>
        <ScopeAccessTable
          allGrants={allGrants}
          assignedGroups={assignedGroups}
          assignedUsers={assignedUsers}
          roleOptions={roleOptions}
          allGroups={allGroups}
          allUsers={allUsers}
          customRoles={customRoles}
          canManage={canManage}
          compact
          onOpenGroup={onOpenGroup}
          onOpenUser={onOpenUser}
          onOpenServiceAccount={onOpenServiceAccount}
          includeServiceAccounts
          commitGrants={(g) => {
            onEdit?.();
            commitGrants(g);
          }}
          removeGroup={(groupId) => {
            onEdit?.();
            removeGroup(groupId);
          }}
          removeUser={(userId) => {
            onEdit?.();
            removeUser(userId);
          }}
          allSpaces={addableSpaces}
          stagedGrantsRef={stagedGrantsRef}
          newPrincipalIds={newPrincipalIds}
          defaultRole={defaultRoleForScope(scopeKind, roleOptions)}
          spacesGainingAccess={(stagedGrants) =>
            spacesGainingSupplierAccess(
              supplierRelation,
              sharedSpaces,
              scopeGrants,
              stagedGrants,
              userGroupMemberships,
              customRoles
            ).map((space) => ({
              id: space.id,
              name: displaySpaceName(space),
              mode: space.mode,
            }))
          }
        />
        <Text
          variant="text-small"
          color="var(--text-secondary)"
          style={{ paddingLeft: 8, marginTop: -8 }}
        >
          {isOnlyCurrentUser
            ? `Only you can edit and use this ${resourceLabel}.`
            : `These users and groups can edit or use the ${resourceLabel} in the spaces below.`}
        </Text>
        {accessFooter}
      </Card>

      {children}

      <Card>
        <SettingsHeading>
          <Text variant="text-regular-md">Spaces</Text>
          <Text variant="text-small" color="var(--text-secondary)">
            Spaces that can use this {resourceLabel} — i.e., every user or group
            with space access also has {resourceLabel} access.
          </Text>
        </SettingsHeading>
        <UsableSpacesList
          spaces={usableSpaces}
          hiddenCount={hiddenSpaceCount}
          resourceLabel={resourceLabel}
          removableIds={removableSpaceIds}
          onRemove={canManage ? setRemovingSpaceId : undefined}
        />
        <ConfirmModal
          open={removingSpace !== null}
          onClose={() => setRemovingSpaceId(null)}
          onConfirm={confirmRemoveSpace}
          title={`Remove ${removingSpace ? displaySpaceName(removingSpace) : "space"}’s access?`}
          description={
            <RemovalDescription
              target={`this ${resourceLabel}`}
              principals={removedPrincipals}
              workflows={removalImpact?.workflows.length ?? 0}
            />
          }
          confirmLabel="Remove"
          destructive
        >
          <DisconnectWarning
            principals={removedPrincipals}
            workflows={removalImpact?.workflows ?? []}
            gitSpaceNames={removalImpact?.gitSpaceNames ?? []}
            chatCount={chatCount}
          />
        </ConfirmModal>
      </Card>
    </TabRoot>
  );
}
